import {useState} from 'react'
import {List} from 'immutable'
import PropTypes from 'prop-types'

import ErrorIcon from '@mui/icons-material/Error'

import {CopyCodesIcon} from './CopyCodesIcon'
import {DownloadCodesIcon} from './DownloadCodesIcon'
import Loader from '../../../../../shared_components/NewLoader'
import {PrintCodesIcon} from './PrintCodesIcon'
import TfaWizardStepFrame from './TfaWizardStepFrame'
import {findResourceByAttributeId} from '../../../../../lib/plan_data/userData'

import checkMark from '../../../../assets/images/check-mark.svg'

const BackupCode = props => {
  const [acknowledged, setAcknowledged] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [loading, setLoading] = useState(false)

  const tfaEnrollment = findResourceByAttributeId({resourceList: props.tfaEnrollments, attribute: 'mode', id: props.enrollmentMode})
  const backupCodeSegments = props.backupCodes || []

  const handleSubmit = event => {
    if (event) event.preventDefault()
    setLoading(true)
    const payload = {tfaConfigId: props.tfaConfig.id, enabled: true, enrollmentId: tfaEnrollment.get('id')}

    props.toggleTfaStatus({
      variables: {input: payload},
      onCompleted: () => { props.nextStep('ENABLED') }
    })
  }

  const handleCopyCodes = async () => {
    if (!(navigator.clipboard && navigator.clipboard.writeText))
      return

    await navigator.clipboard.writeText(backupCodeSegments.join('\n'))
    setFeedback('Copied')
  }

  const handleDownload = () => {
    if (!(window.Blob && window.URL && window.URL.createObjectURL))
      return

    const file = new Blob([backupCodeSegments.join('\n')], {type: 'text/plain;charset=utf-8'})
    const downloadUrl = window.URL.createObjectURL(file)
    const anchor = document.createElement('a')

    anchor.href = downloadUrl
    anchor.download = 'everplans-backup-codes.txt'
    anchor.click()
    window.URL.revokeObjectURL(downloadUrl)
  }

  return (
    <Loader loading={backupCodeSegments.length === 0}>
      <TfaWizardStepFrame
        currentStep={4}
        onPrimaryAction={handleSubmit}
        onSecondaryAction={props.onBack}
        primaryActionLabel='Continue'
        primaryDisabled={!acknowledged}
        primaryLoading={loading}
        secondaryActionLabel='Back'
        subtitle='Store these codes in a safe place. You can use them to access your account if you lose your device.'
        title='Save your backup codes'>
        <div className='tfa-backup-step'>
          <div className='tfa-backup-warning'>
            <ErrorIcon color='error' fontSize='small' />
            <span>
              <strong>Important:</strong>
              {' '}
              Each code can only be used once. Save them now - you won't be able to see them again.
            </span>
          </div>
          <div className='tfa-backup-code-board'>
            <div className='tfa-backup-code-grid'>
              {
                backupCodeSegments.map(segment => (
                  <div className='tfa-backup-code-cell' key={segment}>{segment.match(/(.{1,4})/g).join('\u2015')}</div>
                ))
              }
            </div>
            <div className='tfa-backup-actions-row'>
              <button className='tfa-backup-action-btn' onClick={handleCopyCodes} type='button'>
                <CopyCodesIcon />
                <span>{feedback || 'Copy all codes'}</span>
              </button>
              <button className='tfa-backup-action-btn' onClick={() => window.print()} type='button'>
                <PrintCodesIcon />
                <span>Print</span>
              </button>
              <button className='tfa-backup-action-btn' onClick={handleDownload} type='button'>
                <DownloadCodesIcon />
                <span>Download as text file</span>
              </button>
            </div>
          </div>
          <div className='tfa-backup-guidance'>
            <h4 className='tfa-backup-guidance-title'>Where should I store these codes?</h4>
            <ul>
              <li>
                <img src={checkMark} alt='' aria-hidden='true' />
                Password manager (1Password, LastPass, Bitwarden)
              </li>
              <li>
                <img src={checkMark} alt='' aria-hidden='true' />
                Printed and stored in a safe place
              </li>
              <li>
                <img src={checkMark} alt='' aria-hidden='true' />
                Encrypted cloud storage
              </li>
            </ul>
          </div>
          <label className='tfa-backup-checkbox-row'>
            <input
              checked={acknowledged}
              onChange={event => setAcknowledged(event.target.checked)}
              type='checkbox'
            />
            <span>I have saved these backup codes in a secure location</span>
          </label>
        </div>
      </TfaWizardStepFrame>
    </Loader>
  )
}

BackupCode.propTypes = {
  backupCodes: PropTypes.arrayOf(PropTypes.string),
  enrollmentId: PropTypes.string,
  enrollmentMode: PropTypes.string,
  tfaEnrollments: PropTypes.instanceOf(List),
  nextStep: PropTypes.func,
  onBack: PropTypes.func,
  tfaConfig: PropTypes.shape({id: PropTypes.number}),
  toggleTfaStatus: PropTypes.func.isRequired
}

export default BackupCode
